// src/services/AuthService.ts
import axios from 'axios'
import { API_ORIGIN } from '@/config/api'
import { api, authApi } from './apiClient'
import {
  clearAllTokens,
  clearToken,
  getRefreshToken,
  getToken as getTokenFromStorage,
  setRefreshToken,
  setToken,
} from './token'

export type Me = {
  id: string
  firstName: string
  lastName: string
  secondLastName: string | null
  email: string
  phoneNumber: string | null
  isActive: boolean
  isPrimaryContact: boolean
  inviterEmail: string | null
  username: string | null
  createdAt: string
  municipalityId: string | null
  organizationId: string | null
  partnerId: string | null
  roles: string[]
  permissions: {
    access: string[]
    invite: string[]
    other: string[]
    unknown: string[]
  }
  scopeType: 'organization' | 'municipality' | 'partner' | 'global'
  scopeId: string
  scopeName: string
  themePreference: 'dark' | 'light'
  languagePreference: 'es' | 'en' | 'pt'
}

export async function login(username: string, password: string) {
  const params = new URLSearchParams()
  params.append('username', username)
  params.append('password', password)

  const { data } = await authApi.post('auth/login', params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })

  // Store both tokens
  setToken(data.access_token)
  if (data.refresh_token) {
    setRefreshToken(data.refresh_token)
  }

  return data
}

export async function logout() {
  const refreshToken = getRefreshToken()

  // Try to revoke the refresh token on the server
  if (refreshToken) {
    try {
      await axios.post(
        `${API_ORIGIN}/auth/logout`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      )
    } catch (error) {
      // Ignore errors - we're logging out anyway
      console.warn('Failed to revoke refresh token:', error)
    }
  }

  // Clear local tokens
  clearAllTokens()

  // Redirect to login
  window.location.href = '/login'
}

export async function logoutAllSessions() {
  try {
    await api.post('/auth/logout/all')
  } catch (error) {
    console.warn('Failed to logout all sessions:', error)
  }

  // Clear local tokens
  clearAllTokens()

  // Redirect to login
  window.location.href = '/login'
}

export async function register(payload: any) {
  const { data } = await axios.post(`${API_ORIGIN}/auth/register`, payload)

  // Store both tokens
  if (data.access_token) {
    setToken(data.access_token)
  }
  if (data.refresh_token) {
    setRefreshToken(data.refresh_token)
  }

  return data
}

export async function setActiveScope(scope: {
  scope_type: 'municipality' | 'organization' | 'partner' | 'global'
  scope_id: string | null
}) {
  const { data } = await authApi.post('/auth/scope/set', scope)

  // Store both tokens
  if (data.access_token) {
    setToken(data.access_token)
  }
  if (data.refresh_token) {
    setRefreshToken(data.refresh_token)
  }

  return data
}

export async function fetchMe() {
  const { data } = await api.get<Me>('/users/me')
  return data
}

// Legacy default export for map layer compatibility
const authService = {
  getToken: getTokenFromStorage,
  getRefreshToken,
  deleteToken: clearToken,
  clearAllTokens,
  setToken,
  setRefreshToken,
  login,
  logout,
  logoutAllSessions,
  register,
  setActiveScope,
  fetchMe,
}

export default authService
